import { cn } from '@/_shared/lib/utils';

import { scheduleLabel, scheduleShortLabel } from '../lib/run-view';
import type { SyncSchedule } from '../types';

interface ScheduleLabelProps {
  schedule: SyncSchedule | undefined;
  /** The pipeline's on/off state — NOT a property of the schedule. See the note on muting below. */
  enabled: boolean;
  /**
   * The short form — `24 hours` rather than `Syncs every 24 hours`. For the table's 6.5rem column;
   * the card has room for the sentence. The full form is on the `title` either way, so the compact
   * cell never withholds anything.
   */
  compact?: boolean;
  className?: string;
}

/**
 * A sync's cadence, wherever it is stated. One component because the RULES are the interesting part
 * and they were being restated per surface, which is how the two views came to disagree.
 *
 * **No icon.** The column had a clock for manual and a repeat arrow otherwise, and the clock was
 * actively wrong — a manual sync is the one with NO schedule, sitting beside rows that say "24 hours".
 * Replacing it with a better glyph raised the real question: the cell already says "Manual" or
 * "24 hours" in words, so the icon was a second encoding of a fact the reader had just read.
 *
 * **A disabled sync states its cadence too, muted — it does not get an em dash.** It has a schedule;
 * it is simply not acting on it, and hiding that was wrong three ways:
 *
 *   · The Schedule FILTER classifies on the schedule and ignores `enabled` (`scheduleKind`), so
 *     filtering by Automatic kept rows whose cell was blank. Measured in the app when the table still
 *     did this: 11 of 17 rows matched and 9 showed a dash. The filter and the cell disagreeing about
 *     one fact is the same defect the audiences status badge was fixed for.
 *   · The row already says it is disabled twice — the strip's pilot light is hollow, and the row's
 *     accessible name carries the state. A third statement adds nothing.
 *   · `—` means "no value exists" in the Updated column. Here it meant "a value exists and we are
 *     withholding it". One glyph, two meanings, one table.
 *
 * The card used to keep the dash after the table stopped, on the reasoning that swapping only the
 * chip's text would leave a cadence glyph beside it. There is no glyph any more, so that reason is
 * gone — and the filter defect it left behind was not.
 *
 * What the reader is left with is the pairing: a hollow pilot light next to a cadence reads as "set to
 * this, currently paused", which is true and is also what you want when deciding what to resume.
 */
export function ScheduleLabel({ schedule, enabled, compact, className }: ScheduleLabelProps): React.ReactElement {
  const full = scheduleLabel(schedule);
  return (
    <span
      className={cn('block truncate', enabled ? 'text-foreground' : 'text-muted-foreground', className)}
      // The compact form drops the verb and the start time, so the full sentence lives here — a
      // shortened cadence is never a cadence the reader cannot recover.
      title={enabled ? full : `${full} — paused while disabled`}
    >
      {compact ? scheduleShortLabel(schedule) : full}
    </span>
  );
}
